import { COMMENT_MAX, roleLabel, timeAgo, type Rfq, t, intlLocale } from '@procumi/shared';
import { useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';
import { currentUser } from '../lib/me';
import { Avatar } from './Avatar';

/**
 * Discuția echipei pe cerere (decizia proprietarului, 2026-10-10): doar
 * internă, furnizorul n-o vede. La fiecare comentariu, responsabilii de
 * achiziții ai șantierului și autorul cererii primesc un mail.
 */
export function Comments({
  rfq,
  onUpdated,
  toast,
}: {
  rfq: Rfq;
  onUpdated: (rfq: Rfq) => void;
  toast: Toast;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const me = currentUser();
  const list = rfq.comments ?? [];
  const now = Date.now();

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      onUpdated(await api.addComment(rfq.id, body));
      setText('');
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    const yes = await ask({
      title: t('Ștergi comentariul?'),
      message: t('Dispare din discuție. Cine a primit deja mailul îl păstrează.'),
      confirm: t('Șterge'),
      danger: true,
    });
    if (!yes) return;
    try {
      onUpdated(await api.deleteComment(rfq.id, id));
    } catch (e) {
      toast(errMsg(e));
    }
  };

  return (
    <section className="sheet panel comments" aria-labelledby="comments-h">
      <header className="panel-h">
        <h2 id="comments-h">{t('Discuție')}</h2>
        {list.length ? <span className="panel-hint">{list.length}</span> : null}
        <span className="panel-act comments-note">{t('Doar echipa ta vede discuția')}</span>
      </header>
      {list.length ? (
        <ol className="cm-list">
          {list.map((c) => (
            <li key={c.id} className="cm">
              {c.author ? (
                <Avatar name={c.author.name} userId={c.author.id} avatarAt={c.author.avatarAt} />
              ) : (
                <span className="av none" />
              )}
              <div className="cm-b">
                <div className="cm-h">
                  <b>{c.author?.name ?? t('Fost coleg')}</b>
                  {c.author ? <span className="cm-role">{t(roleLabel(c.author.role))}</span> : null}
                  <span className="cm-when" title={new Date(c.createdAt).toLocaleString(intlLocale())}>
                    {timeAgo(c.createdAt, now)}
                  </span>
                  {c.author?.id === me.id || me.isAdmin ? (
                    <button className="cm-x" onClick={() => void remove(c.id)}>
                      {t('Șterge')}
                    </button>
                  ) : null}
                </div>
                <p>{c.body}</p>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="cm-empty">
          {t(
            'Lasă o notă pentru colegi: ce ai negociat, de ce alegi un furnizor, ce trebuie verificat. Responsabilii șantierului primesc un mail.',
          )}
        </p>
      )}
      <form
        className="cm-new"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <Avatar name={me.name || '?'} userId={me.id} avatarAt={me.avatarAt} />
        <textarea
          className="inp"
          rows={2}
          maxLength={COMMENT_MAX}
          placeholder={t('Scrie un comentariu…')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Ctrl/⌘ + Enter trimite, ca în programele de mail
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button className="btn primary" disabled={busy || !text.trim()}>
          {busy ? t('Se trimite…') : t('Trimite')}
        </button>
      </form>
    </section>
  );
}
